/*
 *  Copyright (c) 2018-present, Evgeny Nadymov
 *
 * This source code is licensed under the GPL v.3.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import AppStore from '../../Stores/ApplicationStore';
import TdLibController from '../../Controllers/TdLibController';
import { md5hex } from '../../Utils/Md5';

class AccountPassword extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            mode: 'login', // login | register
            account: '',
            password: '',
            userName: '',
            registerPassword: '',
            phoneNumber: '',
            inviteCode: '',
            error: null,
            loading: false
        };
    }

    componentDidMount() {
        AppStore.on('clientUpdateSetPhoneError', this.onClientUpdateSetPhoneError);
        AppStore.on('clientUpdateSetPhoneResult', this.onClientUpdateSetPhoneResult);
    }

    componentWillUnmount() {
        AppStore.off('clientUpdateSetPhoneError', this.onClientUpdateSetPhoneError);
        AppStore.off('clientUpdateSetPhoneResult', this.onClientUpdateSetPhoneResult);
    }

    onClientUpdateSetPhoneError = update => {
        const { error } = update;

        let errorString = null;
        if (error && error['@type'] === 'error' && error.message) {
            errorString = error.message;
        } else if (error) {
            errorString = JSON.stringify(error);
        }

        this.setState({ error: errorString, loading: false });
    };

    onClientUpdateSetPhoneResult = () => {
        this.setState({ loading: false });
    };

    handleLogin = () => {
        const { account, password } = this.state;

        if (!account || !account.trim()) {
            this.setState({ error: '请输入用户名或手机号' });
            return;
        }
        if (!password) {
            this.setState({ error: '请输入密码' });
            return;
        }

        this.setState({ error: null, loading: true });
        AppStore.setAccountPassword(account.trim(), md5hex(password));
    };

    handleRegister = () => {
        const { userName, registerPassword, phoneNumber, inviteCode } = this.state;

        if (!userName || !userName.trim()) {
            this.setState({ error: '请输入用户名' });
            return;
        }
        if (!registerPassword) {
            this.setState({ error: '请输入密码' });
            return;
        }
        if (!phoneNumber || !phoneNumber.trim()) {
            this.setState({ error: '请输入手机号' });
            return;
        }

        this.setState({ error: null, loading: true });
        AppStore.setAccountRegister({
            userName: userName.trim(),
            password: registerPassword,
            phoneNumber: phoneNumber.trim(),
            inviteCode: (inviteCode || '').trim()
        });
    };

    handleKeyPress = event => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        const { mode } = this.state;
        if (mode === 'login') {
            this.handleLogin();
        } else {
            this.handleRegister();
        }
    };

    render() {
        const { t } = this.props;
        const {
            mode,
            account,
            password,
            userName,
            registerPassword,
            phoneNumber,
            inviteCode,
            error,
            loading
        } = this.state;

        const title = mode === 'login' ? '账号密码登录' : '注册新账号';

        return (
            <form className='auth-root' autoComplete='off'>
                <Typography variant='body1' className='auth-title'>
                    <span>{title}</span>
                </Typography>
                <Typography variant='body1' className='auth-subtitle' style={{ width: 264 }}>
                    {mode === 'login' ? '请输入用户名（或手机号）与密码' : '请填写注册信息'}
                </Typography>
                {mode === 'login' ? (
                    <>
                        <TextField
                            id='account'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='用户名或手机号'
                            disabled={loading}
                            error={Boolean(error)}
                            helperText={error}
                            fullWidth
                            autoFocus
                            autoComplete='off'
                            value={account}
                            onChange={e => this.setState({ account: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                        <TextField
                            id='account-password'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='密码'
                            type='password'
                            disabled={loading}
                            fullWidth
                            autoComplete='off'
                            value={password}
                            onChange={e => this.setState({ password: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                    </>
                ) : (
                    <>
                        <TextField
                            id='register-username'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='用户名'
                            disabled={loading}
                            error={Boolean(error)}
                            helperText={error}
                            fullWidth
                            autoFocus
                            autoComplete='off'
                            value={userName}
                            onChange={e => this.setState({ userName: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                        <TextField
                            id='register-password'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='密码'
                            type='password'
                            disabled={loading}
                            fullWidth
                            autoComplete='off'
                            value={registerPassword}
                            onChange={e => this.setState({ registerPassword: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                        <TextField
                            id='register-phone'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='手机号'
                            disabled={loading}
                            fullWidth
                            autoComplete='off'
                            value={phoneNumber}
                            onChange={e => this.setState({ phoneNumber: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                        <TextField
                            id='register-invite'
                            classes={{ root: 'auth-input' }}
                            variant='outlined'
                            color='primary'
                            label='邀请码（选填）'
                            disabled={loading}
                            fullWidth
                            autoComplete='off'
                            value={inviteCode}
                            onChange={e => this.setState({ inviteCode: e.target.value })}
                            onKeyPress={this.handleKeyPress}
                        />
                    </>
                )}
                <Button
                    classes={{ root: 'auth-button' }}
                    variant='contained'
                    disableElevation
                    fullWidth
                    color='primary'
                    disabled={loading}
                    onClick={mode === 'login' ? this.handleLogin : this.handleRegister}>
                    {loading ? '请稍候...' : mode === 'login' ? '登录' : '注册'}
                </Button>
                <Typography className='sign-in-continue-on'>
                    <Link
                        onClick={() =>
                            this.setState({ mode: mode === 'login' ? 'register' : 'login', error: null })
                        }>
                        {mode === 'login' ? '没有账号？点击注册' : '已有账号？返回登录'}
                    </Link>
                </Typography>
            </form>
        );
    }
}

AccountPassword.propTypes = {};

export default withTranslation()(AccountPassword);
